import React, { useEffect, useState } from 'react'

// App message cards (bottom-right). Listens for 'app-toast' window events (see lib/toast.js).
// A card: coloured icon · bold title · optional detail line · optional small grey technical reason.
// Errors stay until closed; the rest close by themselves after long enough to read.
const KIND = {
  success: { fg: '#27500a', bg: '#EAF3DE', icon: 'M5 12.5l4.2 4.2L19 7' },
  warning: { fg: '#854F0B', bg: '#FAEEDA', icon: 'M12 7v6M12 16.5v.5' },
  error:   { fg: '#A32D2D', bg: '#FCEBEB', icon: 'M8 8l8 8M16 8l-8 8' },
  info:    { fg: '#0C447C', bg: '#E6F1FB', icon: 'M12 11v6M12 7.5v.5' },
}
const MAX_SHOWN = 4

function lifetime(t) {
  if (t.type === 'error') return null                          // stays until closed
  if (t.type === 'warning') return 8000
  return Math.min(8000, 4000 + 40 * (t.title.length + t.detail.length))
}

export default function Toaster() {
  const [toasts, setToasts] = useState([])

  useEffect(() => {
    const timers = new Set()
    const onToast = (e) => {
      const t = e.detail
      setToasts(list => [...list, t].slice(-MAX_SHOWN))
      const ms = lifetime(t)
      if (ms) {
        const timer = setTimeout(() => { timers.delete(timer); setToasts(list => list.filter(x => x.id !== t.id)) }, ms)
        timers.add(timer)
      }
    }
    window.addEventListener('app-toast', onToast)
    return () => { window.removeEventListener('app-toast', onToast); timers.forEach(clearTimeout) }
  }, [])

  const dismiss = (id) => setToasts(list => list.filter(x => x.id !== id))

  return (
    <div aria-live="polite" style={{
      position: 'fixed', right: 16, bottom: 16, zIndex: 99999,
      display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
      width: 'min(380px, calc(100vw - 32px))', pointerEvents: 'none',
    }}>
      {toasts.map(t => {
        const k = KIND[t.type] || KIND.info
        return (
          <div key={t.id} role={t.type === 'error' ? 'alert' : 'status'} style={{
            pointerEvents: 'auto', width: '100%', boxSizing: 'border-box',
            background: '#fff', color: 'var(--text)', border: '0.5px solid var(--border2)', borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '12px 12px 12px 14px',
            display: 'flex', gap: 12, alignItems: 'flex-start', animation: 'toastIn 0.18s ease-out',
          }}>
            <span style={{ flex: 'none', width: 24, height: 24, borderRadius: '50%', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={k.fg} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={k.icon} /></svg>
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, wordBreak: 'break-word' }}>{t.title}</div>
              {t.detail && <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.45, marginTop: 2, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{t.detail}</div>}
              {t.tech && <div style={{ fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.4, marginTop: 4, wordBreak: 'break-word' }}>{t.tech}</div>}
            </div>
            <button type="button" aria-label="Close" onClick={() => dismiss(t.id)} style={{
              flex: 'none', border: 'none', background: 'none', padding: 2, margin: '-2px -2px 0 0', cursor: 'pointer',
              color: 'var(--text3)', fontSize: 18, lineHeight: 1, minHeight: 0,
            }}>×</button>
          </div>
        )
      })}
    </div>
  )
}
